import { Box, Grid, IconButton, TextField, Theme, Typography } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { makeStyles } from '@material-ui/styles';
import { debounce } from 'lodash';
import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app';
import { CustomerRootDrawer } from '../CustomerDrawer/CustomerRootDrawer';
import CustomerRootPageDrawer from '../CustomerDrawer/CustomerRootPageDrawer';
import CustomerTable from '../CustomerTable/CustomerTable';
import { showCustomerDrawer } from './customersSlice';
import { GetCustomersV2Request, GetCustomersV2Response } from 'common/models/customer';
import AddIcon from '@mui/icons-material/Add';
import CustomerAPI from 'components/customer/api/CustomerAPI';
import { showSnackbar } from 'components/root-snackbar';

const useStyles = makeStyles((theme: Theme): any => ({
  primaryAddBtn: {
    background: theme.palette.primary.main,
    color: theme.palette.common.white,
    '&:hover': {
      background: theme.palette.primary.dark,
    },
  },
}));

export function Customers(): ReactElement {
  const classes: any = useStyles();
  const dmsTypeId = useAppSelector((state) => state.user.dmsTypeId);
  const dispatch = useAppDispatch();
  const isCustomerAddedOrUpdated = useAppSelector(state => state.customers.isCustomerAddedOrUpdated);
  const prevLoading = useRef(isCustomerAddedOrUpdated);

  const [isLoading, setLoading] = useState<boolean>(false);
  const [getCustomersProps, setGetCustomersProps] = useState<GetCustomersV2Request>({
    pageNumber: 1,
    pageSize: 15,
    searchString: '',
  });
  const [customer, setGetCustomersV2] = useState<GetCustomersV2Response>({
    customers: [],
    totalRecords: 0,
    pageNumber: getCustomersProps.pageNumber,
    pageSize: getCustomersProps.pageSize,
  });

  const fetchCustomers = async (): Promise<void> => {
    setLoading(true);
    try {
      const response = await CustomerAPI.getCustomersV2(getCustomersProps);
      if (response) {
        setGetCustomersV2(response);
      } else {
        console.warn('No data returned from the API.');
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
      dispatch(showSnackbar({ type: 'error', message: 'Failed to fetch customers' }));
    } finally {
      setLoading(false);
    }
  };
  const handleSearchCustomer = (searchString: string): void => {
    setGetCustomersProps((prev) => ({
      ...prev,
      searchString,
      pageNumber: 1, // Reset to first page on new search
    }));
  };

  const debouncedFetchCustomers = useCallback(
    debounce(() => {
      fetchCustomers();
    }, 1000),
    [getCustomersProps]
  );

  useEffect(() => {
    if(getCustomersProps.searchString.length > 0) {
      debouncedFetchCustomers();
    } else {
      fetchCustomers();
    }
    return () => {
      debouncedFetchCustomers.cancel();
    };
  }, [getCustomersProps, debouncedFetchCustomers]);

   useEffect(() => {
    if (prevLoading.current && !isCustomerAddedOrUpdated) {
      fetchCustomers();
    }
    prevLoading.current = isCustomerAddedOrUpdated;
  }, [isCustomerAddedOrUpdated]);

  const { drawerType } = useAppSelector((state) => ({
    drawerType: state.customers.drawerType,
  }));
  let open = true;

  if (!drawerType) open = false;
  const onAddCustomerBtn = () => dispatch(showCustomerDrawer({ drawerType: 'CUSTOMER_ADD', drawerProps: {} }));

  return (
    <Box style={{ height: 'auto' }}>
      <Box mt={2}>
        <Grid container style={{ display: 'flex', flexWrap: 'inherit' }}>
            <IconButton
              style={{ height: '49.67px' }}
              title="New Customer"
              className={classes.primaryAddBtn}
              onClick={(): void => {
                onAddCustomerBtn();
              }}
              size="medium">
              <Box sx={{ flexDirection: 'column', alignItems: 'center' }}>
                <AddIcon />
                <Typography style={{ fontSize: '8px', marginTop: '-12px' }}>New</Typography>
              </Box>
            </IconButton>          

          <Grid item xs={12} style={{ paddingLeft: '1rem' }}>
            <TextField
              fullWidth
              style={{ backgroundColor: 'white' }}
              margin="dense"
              variant="outlined"
              name="searchText"
              id="search-text"
              placeholder="Search customers"
              value={getCustomersProps.searchString}
              InputProps={{
                startAdornment: <SearchIcon color="disabled" style={{ paddingRight: '5px' }} />,
              }}
              onChange={({ target: { value } }): void => {
                handleSearchCustomer(value);
              }}
            />
          </Grid>
        </Grid>
      </Box>
      <Box mt={2}>
        <CustomerTable loading={isLoading} customer={customer} getCustomersProps={getCustomersProps} setGetCustomersProps={setGetCustomersProps} />
      </Box>
      {<CustomerRootPageDrawer />}
      {<CustomerRootDrawer />}
    </Box>
  );
}

export default Customers;
